import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import { useGetLabelByKey, useGetLabelsByKeys } from 'hooks/useLingUI';
import { SkeletonArea } from 'components/skeletonArea';
import { SportResultsAndInfoContent } from 'features/sport/components/sportResultsAndInfoContent';
import { StatisticType } from 'features/sport/utils/betRadar/statisticTypeEnum';
import { isSnaiSite } from 'utility/constant';
import { selectInfoTemplateManifestazione } from 'features/sport/selectors';
import styles from './SportMarcatoriTableStats.module.scss';
import { useBlockScroll } from 'hooks/useBlockScroll';
import { useGetStatsMarcatoriQuery } from 'features/api/sportApiSlice';
import { usePlayerData } from 'features/sport/hooks';
import { useTypedSelector } from 'lib/centralStore';
import { Modal } from 'components/modals/components/modal';
import { ReactPortal } from 'components/reactPortal';
import { SimpleInfoModalBody } from 'components/modals/simpleInfoModal/components/simpleInfoModalBody';

export type ScommesseTableStatsProps = {
  title?: string;
  isOpen: boolean;
  playerKey: string;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const SportMarcatoriTableModalContent = ({ slug }: { slug: string }) => {
  const { templateManifestazione: template } = useTypedSelector(selectInfoTemplateManifestazione);
  const manifKey = `${template?.idDisciplina}-${template?.idManifestazione}`;
  const { data, isFetching, isError } = useGetStatsMarcatoriQuery({ idManifestazione: manifKey, slug });
  const [noDataPlayer] = useGetLabelByKey('no-data-player');

  const objIcon = useMemo(() => {
    const src = isSnaiSite ? IconsEnum.SNAI_LOGO_SHORT : IconsEnum.HAPPYBET_LOGO;
    return <Icon iconId={src} className={styles.icon} color="var(--color-on-bg-primary)" />;
  }, []);

  if (isFetching) {
    return <SkeletonArea fullHeight />;
  }
  if (isError) {
    return (
      <div className={styles.container}>
        {objIcon}
        <p className={styles.text}>{noDataPlayer}</p>
      </div>
    );
  }

  return (
    <SportResultsAndInfoContent
      id={Number(`${data?.playerId}`)}
      type={StatisticType.Player}
      isOpen={true}
      teamUid={Number(data?.teamUid ?? 0)}
      playerId={Number(data?.playerId ?? 0)}
      seasonId={Number(data?.seasonId ?? 0)}
      uniqueTournamentId={Number(data?.uniqueTournamentId ?? 0)}
      key={`${data?.playerId}-${data?.seasonId}-${data?.uniqueTournamentId}-${data?.teamUid}`}
    />
  );
};

export const SportMarcatoriTableStats = ({ isOpen, setIsOpen, title, playerKey }: ScommesseTableStatsProps) => {
  const [resultAndInfoLabel] = useGetLabelsByKeys(['risultati-ed-info']);
  const { slug } = usePlayerData(playerKey);

  useBlockScroll(isOpen);

  if (isOpen && slug) {
    return (
      <ReactPortal wrapperId="sport-marcatori-stats" data-key="react-portal">
        <Modal
          containerClass="sport-marcatori-stats"
          label={title ? title : resultAndInfoLabel}
          modalType="tertiary"
          modalCategory="simple-modal"
          onClose={() => setIsOpen(false)}
        >
          <SimpleInfoModalBody>
            <SportMarcatoriTableModalContent slug={slug} key={slug} />
          </SimpleInfoModalBody>
        </Modal>
      </ReactPortal>
    );
  }

  return null;
};